/**
 * useClientContext — ambient client identification + manual search.
 *
 * Ported from v1 (mcp/src/outlook-addin/src/hooks/useClientContext.ts) and
 * rewired to v2's orchestratorApi:
 *
 *   v1 api.getClientContext(...)        → orchestratorApi.analyzeEmail(...)
 *                                          + pollAnalysis(instance_id)
 *   v1 api.searchClients(name)          → orchestratorApi.getClientDetails — see notes below
 *   v1 api.getClientContextBySk(sk)     → orchestratorApi.getClientDetails(sk)
 *                                          + getClientPolicies(sk)
 *
 * The manual search path calls `orchestratorApi.searchClientsLegacy(name)` which
 * hits `GET /api/Clients/SearchClientInfo?clientInfo=...` — the same scoped
 * endpoint used by the main Remix app's client search dropdown.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { ClientContext, ClientSearchResult } from "../types/client";
import { orchestratorApi } from "../services/orchestratorApi";
import type { EmailContext } from "./useMailboxItem";


export type ClientContextState =
  | "idle"
  | "searching"
  | "analyzing"
  | "matched"
  | "gathering"
  | "done"
  | "not_found";

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface UseClientContextValue {
  state: ClientContextState;
  clientContext: ClientContext | null;
  searchResults: ClientSearchResult[];
  suggestedResults: ClientSearchResult[];
  searchLoading: boolean;
  searchByName: (name: string) => void;
  selectClient: (clientSk: number) => void;
  /** Select a client from a search result — uses the result's data as a
   *  fallback when getClientDetails returns null (endpoint not yet wired). */
  selectClientFromResult: (result: ClientSearchResult) => void;
  /** Directly set client context from pre-fetched data (e.g. from
   *  analyze_email metadata) without making additional API calls. */
  hydrateFromContext: (ctx: ClientContext) => void;
  /**
   * Immediately transitions to "searching" state (hides the search box,
   * shows the spinner) then searches by name. Auto-selects when the result
   * is unambiguous (exactly 1 match); otherwise reverts to "not_found" with
   * results populated for manual selection.
   */
  beginLookupByName: (name: string) => Promise<void>;
  /** Surface low-confidence multi-match candidates (from analyze_email) as the
   *  not_found "did you mean" picker without any extra API call. The operator
   *  picks one (→ selectClient) or dismisses to the manual search box. */
  showSuggestions: (results: ClientSearchResult[]) => void;
  reset: () => void;
}

export function useClientContext(
  emailContext: EmailContext | null,
): UseClientContextValue {
  const [state, setState] = useState<ClientContextState>("idle");
  const [clientContext, setClientContext] = useState<ClientContext | null>(
    null,
  );
  const [searchResults, setSearchResults] = useState<ClientSearchResult[]>([]);
  const [suggestedResults, setSuggestedResults] = useState<ClientSearchResult[]>(
    [],
  );
  const [searchLoading, setSearchLoading] = useState(false);
  const lastMessageId = useRef<string | null>(null);
  /** Client sk whose policy backfill has already been attempted (see below). */
  const attemptedSkRef = useRef<number | null>(null);
  /** Bumped on every user pick / hydrate so a late name-lookup cannot wipe it. */
  const lookupGenRef = useRef(0);

  // ── Auto path: identify client from selected email ────────────────────────
  useEffect(() => {
    if (!emailContext?.emailId) return;
    if (emailContext.emailId === lastMessageId.current) return;
    lastMessageId.current = emailContext.emailId;

    const controller = new AbortController();
    setClientContext(null);
    setSearchResults([]);
    setSuggestedResults([]);
    setState("searching");

    const run = async () => {
      await delay(400);
      if (controller.signal.aborted) return;
      setState("analyzing");
      // analyzeEmail is handled by useWorkflowStatus — this hook only drives
      // the client-search UI state. Degrade to not_found so the manual search
      // input is shown while the workflow analysis runs in parallel.
      // NB: do NOT clear suggestedResults here — App.tsx may have already fed
      // analyze_email candidates via showSuggestions() on the warm path (the
      // emailId-change reset above is the single clear point per email).
      if (!controller.signal.aborted) {
        setState("not_found");
      }
    };

    void run();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailContext?.emailId]);

  // ── Manual path: search by name ───────────────────────────────────────────
  const searchByName = useCallback(async (name: string) => {
    if (!name.trim()) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const results = await orchestratorApi.searchClientsLegacy(name);
      setSearchResults(results);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  /**
   * Backfill `policies_in_force` when an incoming context arrives without it.
   *
   * analyze_email's `client_context` can land with no policies (the API DTO
   * types `policies_in_force` as NULLABLE while the TS type claims otherwise),
   * and the paths that accept a pre-built context used to trust it verbatim.
   * `selectClient` was the ONLY path that fetched policies explicitly — which is
   * why the tile read "0 policies" on a cold open and repaired itself only after
   * the operator hit Change and re-found the client.
   *
   * Declared BEFORE the callbacks that depend on it: a `const` referenced in a
   * useCallback dep array is evaluated during render, so a later declaration
   * would be a TDZ ReferenceError, not just a lint nit.
   *
   * Fire-and-forget on purpose: the context is set first so the tile renders
   * immediately and policies fill in when they arrive. A failed fetch leaves the
   * thin-but-valid context alone rather than blanking the tile.
   */
  const backfillPolicies = useCallback(async (ctx: ClientContext) => {
    const sk = ctx.client?.client_sk;
    if (!sk) return;
    if ((ctx.policies_in_force?.length ?? 0) > 0) return;
    // Hold the tile in its LOADING state for the duration of the round trip
    // rather than letting it assert "0 policies" and then correct itself
    // (operator feedback 2026-07-28). "gathering" was already declared and
    // already treated as loading by App + LaunchTab — nothing ever set it.
    setState("gathering");
    const policies = await orchestratorApi.getClientPolicies(sk).catch(() => []);
    setClientContext((prev) => {
      // Drop a late response if the operator has since switched clients, or if
      // policies already arrived by another route — never clobber fresher state.
      if (!prev || prev.client?.client_sk !== sk) return prev;
      if ((prev.policies_in_force?.length ?? 0) > 0) return prev;
      return policies.length ? { ...prev, policies_in_force: policies } : prev;
    });
    // ALWAYS settle. An empty or failed fetch must not leave the tile spinning
    // forever — and the drawer's auto-open gate treats "gathering" as unsettled,
    // so a stuck state here would mean the drawer never opens. The updater form
    // avoids stomping a newer state (e.g. the operator started another search).
    setState((prev) => (prev === "gathering" ? "done" : prev));
  }, []);

  const selectClient = useCallback(async (clientSk: number) => {
    lookupGenRef.current += 1;
    setState("searching");
    setSearchResults([]);
    try {
      const [details, policies] = await Promise.all([
        orchestratorApi.getClientDetails(clientSk),
        orchestratorApi.getClientPolicies(clientSk).catch(() => []),
      ]);
      if (details && details.client) {
        setClientContext((prev) => {
          const same = prev?.client.client_sk === details.client.client_sk
            ? prev.client
            : null;
          return {
            client: {
              ...details.client,
              city: details.client.city || same?.city,
              state: details.client.state || same?.state,
              branch_name: details.client.branch_name || same?.branch_name,
            },
            policies_in_force: policies.length > 0 ? policies : details.policies_in_force,
            opportunities: details.opportunities ?? [],
          };
        });
        setState("done");
      } else {
        setState("not_found");
      }
    } catch {
      setState("not_found");
    }
  }, []);

  const selectClientFromResult = useCallback(async (result: ClientSearchResult) => {
    // Invalidate in-flight beginLookupByName so a late 0-match cannot
    // flip the tile back to the empty search box after the AM picked.
    const gen = ++lookupGenRef.current;
    const optimistic: ClientContext = {
      client: {
        client_sk: result.clientSk,
        client_id: "",
        client_name: result.clientName,
        city: result.city,
        state: result.state,
        branch_name: result.branchName,
        client_type: result.clientType,
      },
      policies_in_force: [],
      opportunities: [],
    };
    setClientContext(optimistic);
    setSearchResults([]);
    setSuggestedResults([]);
    setState("done");
    try {
      const details = await orchestratorApi.getClientDetails(result.clientSk);
      if (gen !== lookupGenRef.current) return;
      const next: ClientContext = {
        client: {
          // Use API data for authoritative fields; merge search result for
          // location/branch which the Client entity doesn't store.
          client_sk: result.clientSk,
          client_id: details?.client.client_id ?? "",
          client_name: details?.client.client_name ?? result.clientName,
          city: details?.client.city ?? result.city,
          state: details?.client.state ?? result.state,
          branch_name: details?.client.branch_name ?? result.branchName,
          client_type: details?.client.client_type ?? result.clientType,
        },
        policies_in_force: details?.policies_in_force ?? [],
        opportunities: details?.opportunities ?? [],
      };
      setClientContext(next);
      setState("done");
    } catch {
      if (gen !== lookupGenRef.current) return;
      // Keep the optimistic pick — do not revert to the empty search box.
    }
  }, []);

  const hydrateFromContext = useCallback((ctx: ClientContext) => {
    lookupGenRef.current += 1;
    setClientContext(ctx);
    setState("done");
    setSearchResults([]);
    setSuggestedResults([]);
  }, []);

  /**
   * Reactive backfill: whenever we hold a client with NO policies, fetch them
   * once.
   *
   * Deliberately an effect rather than a call at each entry point. Patching
   * individual call sites (tried first) missed any path that sets the context
   * some other way, and it could not repair a context that was ALREADY in state
   * — so a client hydrated before the fetch existed stayed stuck at
   * "0 policies" forever. Watching the state covers every producer, present and
   * future, and is safe to re-run because the merge guards on sk + emptiness.
   *
   * `attemptedSkRef` stops a client whose fetch legitimately returns nothing
   * from being retried on every unrelated context change.
   */
  useEffect(() => {
    const sk = clientContext?.client?.client_sk;
    if (!sk) return;
    if ((clientContext?.policies_in_force?.length ?? 0) > 0) return;
    if (attemptedSkRef.current === sk) return;
    attemptedSkRef.current = sk;
    void backfillPolicies(clientContext);
  }, [clientContext, backfillPolicies]);

  const beginLookupByName = useCallback(async (name: string) => {
    if (!name.trim()) return;
    const gen = ++lookupGenRef.current;
    setState("searching");
    setSearchResults([]);
    setSuggestedResults([]);
    try {
      const results = await orchestratorApi.searchClientsLegacy(name);
      if (gen !== lookupGenRef.current) return;
      if (results.length === 1) {
        await selectClientFromResult(results[0]);
      } else {
        setSearchResults(results);
        setState("not_found");
      }
    } catch {
      if (gen !== lookupGenRef.current) return;
      setState("not_found");
    }
  }, [selectClientFromResult]);

  const showSuggestions = useCallback((results: ClientSearchResult[]) => {
    setSearchResults([]);
    setSuggestedResults(results);
    setState("not_found");
  }, []);

  const reset = useCallback(() => {
    lookupGenRef.current += 1;
    setState("idle");
    setClientContext(null);
    setSearchResults([]);
    setSuggestedResults([]);
    lastMessageId.current = null;
  }, []);

  return {
    state,
    clientContext,
    searchResults,
    suggestedResults,
    searchLoading,
    searchByName,
    selectClient,
    selectClientFromResult,
    hydrateFromContext,
    beginLookupByName,
    showSuggestions,
    reset,
  };
}
